/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../../../../store/store'
import {
  setIsAuthenticated,
  updateUserAddress,
} from '../../../../../../../../../store/reducers/userReducer'
import { AddressWrapper } from './style'
import Dropdown from '../../../../../../../../../shared/components/dropdown/Dropdown'
import citiesJson from '../../../../../../../../../assets/data/cities.json'
import countriesJson from '../../../../../../../../../assets/data/countries.json'
import { NumberInput } from '../../../../../../../../../shared/components/number-input/NumberInput'
import { useUpdateSfUserMutation } from '../../../../../../../../../store/api-slices/userSlice'
import { Error } from 'shared/components/error/Error'

import { FormTypes } from 'types/enums/EFormTypes'
import {
  isInitialState,
  useGenericFormState,
} from 'shared/hooks/useGenericState'
import { setFormTranslatedText } from 'pages/auth/utils/setFormTranslatedText'
import { useTranslation } from 'react-i18next'
import { setForm, setIsLayoutLoading } from 'store/reducers/layoutReducer'
import { IAddress } from 'types/interfaces/IAddress'
import { AUTH_USER_TOKEN_KEY } from 'constants/auth'
import Checkbox from 'shared/components/checkbox/Checkbox'
import { TextInput } from 'shared/components/text-input/TextInput'
import { ELanguage } from 'types/enums/ELanguage'
import { useNavigate } from 'react-router-dom'
import { EAppRoutes } from 'types/enums/EAppRoutes'
import { Button } from 'shared/components/button/Button'
import { Cities } from 'types/types/Cities'
import { getToken } from 'services/AuthService'

type Countries = {
  hebName: string
  enName: string
}[]

type ExtraPersonalInfoFormErrors = Partial<
  Record<keyof IAddress, boolean> & { submit: boolean }
>

export const PersonalDetailsFormExtraPersonalInfo: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [updateSfUser] = useUpdateSfUserMutation()

  const address = useAppSelector<IAddress | undefined>(
    (state) => state.user.personalDetails?.addresses?.[0],
  )
  const token = getToken()
  const accountId = useAppSelector((state) => state.user.accountId)
  const contactId = useAppSelector((state) => state.user.contactId)

  const { t, i18n } = useTranslation('', {
    keyPrefix: `pages.signUp.extraPersonalDetails`,
  })
  setFormTranslatedText('signUp.extraPersonalDetails')

  const [
    extraPersonalInfoFormData,
    setExtraPersonalInfoFormData,
    initialExtraPersonalInfoFormData,
  ] = useGenericFormState<IAddress>({
    country: address?.country || t('inputs.country.default'),
    state: address?.state ?? '',
    city: address?.city ?? '',
    street: address?.street ?? '',
    buildingNumber: address?.buildingNumber ?? '',
    postalCode: address?.postalCode ?? '',
  })
  const [errors, setErrors] = useState<ExtraPersonalInfoFormErrors>({
    country: false,
    state: false,
    city: false,
    street: false,
    postalCode: false,
  })
  const [countries, setCountries] = useState<Countries>([])
  const [cities, setCities] = useState<Cities>({})
  const [streets, setStreets] = useState<string[]>([])
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [submitError, setSubmitError] = useState<boolean>(false)

  const [isForeignAddress, setIsForeignAddress] = useState<boolean>(false)
  const [cityHebName, setCityHebName] = useState<string>()

  useEffect(() => {
    setCities(citiesJson)
    setCountries(countriesJson)
    setExtraPersonalInfoFormData({
      ...extraPersonalInfoFormData,
      city: '',
      street: '',
    })
  }, [t])

  useEffect(() => {
    const isFormDataComplete =
      extraPersonalInfoFormData.country &&
      extraPersonalInfoFormData.city &&
      extraPersonalInfoFormData.street &&
      extraPersonalInfoFormData.buildingNumber
    const isValid = Object.values(errors).every((error) => !error)
    if (isFormDataComplete && isValid) {
      setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }
  }, [Object.values(extraPersonalInfoFormData), errors])

  const handleFieldChange = <T extends keyof typeof extraPersonalInfoFormData>(
    field: T,
    value: (typeof extraPersonalInfoFormData)[T],
    error?: boolean,
  ) => {
    setErrors({ ...errors, [field]: error })
    setExtraPersonalInfoFormData({
      ...extraPersonalInfoFormData,
      [field]: value,
    })
  }

  const handleCityFieldChange = (value: string, error?: boolean) => {
    setErrors({ ...errors, city: error })
    setExtraPersonalInfoFormData({
      ...extraPersonalInfoFormData,
      city: value,
      street: '',
    })
  }

  const handleForeignCountryFieldChange = () => {
    setExtraPersonalInfoFormData({
      ...extraPersonalInfoFormData,
      country: isForeignAddress ? t('inputs.country.default') : '',
      city: '',
      street: '',
    })
    setIsForeignAddress((prev) => !prev)
  }

  const nextButtonClicked = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    dispatch(setIsLayoutLoading(true))
    e.preventDefault()

    if (
      isInitialState(
        initialExtraPersonalInfoFormData,
        extraPersonalInfoFormData,
      )
    ) {
      localStorage.setItem(AUTH_USER_TOKEN_KEY, token || '')
      dispatch(setIsAuthenticated(true))
      dispatch(setForm({ formType: FormTypes.SUCCESS_PAGE }))
      return
    }
    const updatedAddress = {
      accountId,
      contactId,
      ...extraPersonalInfoFormData,
      cityHebName,
      isForeignAddress,
    }

    try {
      const sfUser = await updateSfUser(updatedAddress).unwrap()

      const { success } = sfUser
      if (success) {
        localStorage.setItem(AUTH_USER_TOKEN_KEY, token || '')
        dispatch(setIsAuthenticated(true))
        dispatch(updateUserAddress(extraPersonalInfoFormData))
        dispatch(setForm({ formType: FormTypes.LAST }))
        navigate(EAppRoutes.LOGIN)
      }
    } catch (err: any) {
      setSubmitError(true)
      console.log('An error occurred:', err)
    } finally {
      dispatch(setIsLayoutLoading(false))
    }
  }

  useEffect(() => {
    const citiesArray = Object.values(cities)
    const fieldName = i18n.language === ELanguage.HEBREW ? 'hebName' : 'enName'

    const matchingCity = citiesArray.find(
      (city) => city[fieldName] === extraPersonalInfoFormData.city,
    )
    setCityHebName(matchingCity?.hebName || '')
    const streetsArray = matchingCity?.streets || []
    setStreets(streetsArray)
  }, [extraPersonalInfoFormData.city, cities, i18n.language])

  return (
    <>
      <AddressWrapper>
        {isForeignAddress ? (
          <>
            <Dropdown
              value={extraPersonalInfoFormData.country}
              disabled={false}
              hasError={errors.country}
              id={'country'}
              label={t('inputs.country.labelText')}
              errorText={t('inputs.country.foreignErrorText')}
              options={countries.map(
                (country) =>
                  country[
                    i18n.language === ELanguage.HEBREW ? 'hebName' : 'enName'
                  ],
              )}
              callback={(value, error) =>
                handleFieldChange('country', value, error)
              }
            ></Dropdown>
            <TextInput
              value={extraPersonalInfoFormData.state}
              disabled={false}
              hasError={errors.state}
              id={'state'}
              minLength={2}
              label={t('inputs.state.labelText')}
              errorText={t('inputs.state.foreignErrorText')}
              callback={(value, error) =>
                handleFieldChange('state', value, error)
              }
            />
            <TextInput
              value={extraPersonalInfoFormData.city}
              disabled={false}
              hasError={errors.city}
              id={'city'}
              minLength={2}
              label={t('inputs.city.labelText')}
              errorText={t('inputs.city.foreignErrorText')}
              callback={handleCityFieldChange}
            />
          </>
        ) : (
          <Dropdown
            value={extraPersonalInfoFormData.city}
            disabled={false}
            hasError={errors.city}
            id={'city'}
            label={t('inputs.city.labelText')}
            errorText={t('inputs.city.errorText')}
            options={Object.values(cities).map(
              (city) =>
                city[i18n.language === ELanguage.HEBREW ? 'hebName' : 'enName'],
            )}
            callback={handleCityFieldChange}
          />
        )}

        {i18n.language === ELanguage.HEBREW && !isForeignAddress ? (
          <Dropdown
            value={extraPersonalInfoFormData.street}
            disabled={!extraPersonalInfoFormData.city}
            hasError={errors.street}
            id={'street'}
            label={t('inputs.street.labelText')}
            errorText={t('inputs.street.errorText')}
            options={streets}
            callback={(value, error) =>
              handleFieldChange('street', value, error)
            }
          />
        ) : (
          <TextInput
            value={extraPersonalInfoFormData.street}
            disabled={!extraPersonalInfoFormData.city}
            hasError={errors.street}
            id={'street'}
            minLength={2}
            label={t('inputs.street.labelText')}
            errorText={t('inputs.street.foreignErrorText')}
            callback={(value, error) =>
              handleFieldChange('street', value, error)
            }
          />
        )}

        <NumberInput
          value={extraPersonalInfoFormData.buildingNumber ?? undefined}
          regex={RegExp(/^[0-9]{1,9}$/i)}
          hasError={false}
          errorText={''}
          callback={(value, error) =>
            handleFieldChange('buildingNumber', value, error)
          }
          id={'buildingNumber'}
          label={t('inputs.buildingNumber.labelText')}
        ></NumberInput>
        <NumberInput
          value={extraPersonalInfoFormData.postalCode}
          regex={RegExp(/^(|[0-9]{7,9})$/i)}
          hasError={errors.postalCode}
          callback={(value, error) =>
            handleFieldChange(
              'postalCode',
              value,
              isForeignAddress ? false : error,
            )
          }
          id={'postalCode'}
          label={t('inputs.postalCode.labelText')}
          errorText={t('inputs.postalCode.errorText')}
        ></NumberInput>
      </AddressWrapper>
      <Checkbox
        callback={handleForeignCountryFieldChange}
        id="isForeignAddress"
        label={t('inputs.foreignAddress.labelText')}
        checked={isForeignAddress}
      />
      {submitError && (
        <Error hasError={true} errorText={t('buttons.submit.errorText')} />
      )}

      <Button
        type="submit"
        disabled={isDisabled}
        className={`button ${isDisabled ? 'disabled' : ''}`}
        text={t('buttons.submit.text')}
        callback={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
          nextButtonClicked(e)
        }
      />
    </>
  )
}
